import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import { useAddMealToMealPlanByDate } from '../hooks/addMealToMealPlanByDate';
import { useAuth } from '../context/AuthContext';
import CompleteMealCookingButton from '../components/CompleteMealCookingButton';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import supabase from '../supabaseClient';   

function MealSteps({ meal: initialMeal, onBack }) {
    const { pgUser, sessionToken: authToken } = useAuth();

    const { mealId } = useParams();
    const [searchParams] = useSearchParams();
    const [meal, setMeal] = useState(null);
    const [mealData, setMealData] = useState(null);
    const [mealName, setMealName] = useState('');
    const [recipeData, setRecipeData] = useState(null);
    const [steps, setSteps] = useState([]);
    const [ingredients, setIngredients] = useState([]);
    const [servings, setServings] = useState(0);
    const [timing, setTiming] = useState({
        prepTime: 0,
        cookTime: 0,
        totalTime: 0
    });
    const [nutrition, setNutrition] = useState({
        calories: 0,
        protein: 0,
        carbohydrate: 0,
        fat: 0
    });
    const [sessionToken, setSessionToken] = useState(authToken);

    // Add new state for modal
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [selectedPlannedCookedDate, setSelectedPlannedCookedDate] = useState('');
    const [mealType, setMealType] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);

    // Add the meal plan hook
    const { addMealToMealPlanByDate, isLoading, error } = useAddMealToMealPlanByDate();

    // Helper function to format quantity as clean fractions when possible
    const formatQuantity = (num) => {
        const allowedFractions = [
            { fraction: 1/16, label: "1/16", vulgar: "¹⁄₁₆" },
            { fraction: 1/8, label: "1/8", vulgar: "⅛" },
            { fraction: 1/6, label: "1/6", vulgar: "⅙" },
            { fraction: 1/4, label: "1/4", vulgar: "¼" },
            { fraction: 1/3, label: "1/3", vulgar: "⅓" },
            { fraction: 1/2, label: "1/2", vulgar: "½" },
            { fraction: 5/8, label: "5/8", vulgar: "⅝" },
            { fraction: 2/3, label: "2/3", vulgar: "⅔" },
            { fraction: 3/4, label: "3/4", vulgar: "¾" },
            { fraction: 5/6, label: "5/6", vulgar: "⅚" },
        ];

        const parsed = parseFloat(num);
        if (isNaN(parsed)) return num;

        const fractionTolerance = 0.008; // Tolerance to catch common rounding (e.g., 0.33 → 1/3, 0.66 → 2/3)
        const wholeTolerance = 0.02; // Larger tolerance for whole numbers (e.g., 0.99 → 1, 1.98 → 2)
        const whole = Math.floor(parsed);
        const decimal = parsed - whole;

        // Check if it's basically a whole number (including cases like 0.99 or 1.01)
        if (decimal < wholeTolerance || decimal > (1 - wholeTolerance)) {
            return `${decimal > (1 - wholeTolerance) ? whole + 1 : whole}`;
        }

        // Check for fraction match
        const match = allowedFractions.find(f => Math.abs(decimal - f.fraction) < fractionTolerance);

        if (match) {
            const fractionSpan = <span style={{ fontSize: '1.3em' }}>{match.vulgar}</span>;
            if (whole > 0) {
                return <>{whole} {fractionSpan}</>;
            }
            return fractionSpan;
        }

        // Fall back to decimal, cleaned up
        const rounded = parsed.toFixed(3);
        return rounded.replace(/\.00$/, '');
    };

    const [loadingError, setLoadingError] = useState(null);

    // First useEffect to handle session token
    useEffect(() => {
        const validateAndGetSessionToken = async () => {
            // If we already have a valid auth token from context, use it
            if (authToken) {
                setSessionToken(authToken);
                return;
            }

            // Otherwise, try to get a new session token
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.access_token) {
                    setSessionToken(session.access_token);
                } else {
                    console.error('No access token found in session');
                    setLoadingError('Authentication failed');
                }
            } catch (error) {
                console.error('Error getting session:', error);
                setLoadingError('Authentication failed');
            }
        };
        validateAndGetSessionToken();
    }, [authToken]);

    // Second useEffect for fetching meal and recipe data
    useEffect(() => {
        const fetchMealData = async () => {
            if (!sessionToken) {
                return; // Don't proceed without a session token
            }

            // Get meal data either from props or sessionStorage
            const storedMeal = sessionStorage.getItem('currentMeal');
            const mealData = initialMeal || (storedMeal ? JSON.parse(storedMeal) : null);
            console.log("mealData: ", mealData);
            console.log("mealData.has_been_cooked: ", mealData.has_been_cooked);
            
            if (!mealData) {
                console.error('No meal data available');
                setLoadingError('Meal data not available');
                return;
            }

            setMeal(mealData);
            sessionStorage.setItem('currentMeal', JSON.stringify(mealData));

            try {
                // Fetch all data in parallel
                const [recipeResponse, stepsResponse, ingredientsResponse] = await Promise.all([
                    // Fetch recipe details
                    fetchWithCSRF(`/api/v1/recipe/${mealData.recipe_id}`, {
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    }),
                    // Fetch recipe steps
                    fetchWithCSRF(`/api/v1/recipe-step/${mealData.recipe_id}`, {
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    }),
                    // Fetch recipe ingredients
                    fetchWithCSRF(`/api/v1/recipe-ingredient/recipe/${mealData.recipe_id}`, {
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    })
                ]);

                // Check if any requests failed
                if (!recipeResponse.ok) {
                    throw new Error(`Failed to fetch recipe: ${recipeResponse.status}`);
                }
                if (!stepsResponse.ok) {
                    throw new Error(`Failed to fetch steps: ${stepsResponse.status}`);
                }
                if (!ingredientsResponse.ok) {
                    throw new Error(`Failed to fetch ingredients: ${ingredientsResponse.status}`);
                }

                // Parse all responses
                const [recipeData, stepsData, ingredientsData] = await Promise.all([
                    recipeResponse.json(),
                    stepsResponse.json(),
                    ingredientsResponse.json()
                ]);

                // Process recipe data
                if (Array.isArray(recipeData) && recipeData.length > 0) {
                    const recipe = recipeData[0];
                    setRecipeData(recipe);
                    setMealName(recipe.name);
                    setServings(mealData.serving_size);
                    setTiming({
                        prepTime: recipe.prep_time || 0,
                        cookTime: recipe.cook_time || 0,
                        totalTime: (recipe.prep_time || 0) + (recipe.cook_time || 0)
                    });
                    
                    setNutrition({
                        calories: Math.round(parseFloat(recipe.calories) || 0),
                        protein: Math.round(parseFloat(recipe.protein) || 0),
                        carbohydrate: Math.round(parseFloat(recipe.carbohydrate) || 0),
                        fat: Math.round(parseFloat(recipe.fat) || 0)
                    });
                } else {
                    throw new Error('Recipe data not found');
                }

                // Process steps data
                if (Array.isArray(stepsData)) {
                    setSteps(stepsData.sort((a, b) => a.step_number - b.step_number));
                } else {
                    console.error('Invalid steps data format:', stepsData);
                    setSteps([]);
                }

                // Process ingredients data
                if (Array.isArray(ingredientsData)) {
                    setIngredients(ingredientsData);
                } else {
                    console.error('Invalid ingredients data format:', ingredientsData);
                    setIngredients([]);
                }
            } catch (error) {
                console.error('Error fetching recipe data:', error);
                setLoadingError(error.message || 'Failed to load recipe data');
                setMeal(null); // Clear meal data on error to prevent partial display
            }
        };

        fetchMealData();
    }, [initialMeal, sessionToken]);

    // Cleanup function to clear sessionStorage when component unmounts
    useEffect(() => {
        return () => {
            if (onBack) {
                sessionStorage.removeItem('currentMeal');
            }
        };
    }, [onBack]);

    if (!meal) {
        return (
            <div className="container text-center my-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading meal details...</span>
                </div>
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container">
                <div className="alert alert-warning" role="alert">
                    <p className="mb-0">{loadingError}</p>
                    <button 
                        className="btn btn-sm btn-outline-warning mt-2"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0">
            <div className="position-relative mb-3">
                <div 
                    style={{ 
                        width: '100%',
                        height: '60vh',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <img 
                        src={`/images/recipeImages/${meal.recipe_id}.jpg`}
                        alt={mealName}
                        style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                        }}
                    />
                    <div 
                        style={{
                            position: 'absolute',
                            bottom: '2rem',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            color: 'white',
                            textAlign: 'center',
                            width: '100%',
                            padding: '0 1rem'
                        }}
                    >
                        <h1 
                            style={{
                                fontSize: '2.5rem',
                                fontWeight: '600',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                marginBottom: '1rem'
                            }}
                        >
                            {mealName}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="container" style={{ paddingL: '0 1rem' }}>
                <div className="d-flex flex-column align-items-center mb-4">
                    <div className="d-flex flex-column align-items-center mb-4 w-100">
                        <div className="mb-3">
                            <ul className="list-inline mb-0">
                                <li className="list-inline-item me-4">
                                    <span className="text-muted">Prep Time:</span> {timing.prepTime} min
                                </li>
                                <li className="list-inline-item">
                                    <span className="text-muted">Cook Time:</span> {timing.cookTime} min
                                </li>
                            </ul>
                        </div>

                        <div className="d-flex align-items-center gap-4">
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.calories}</div>
                                <div className="text-muted small">calories</div>
                            </div>
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.protein}g</div>
                                <div className="text-muted small">protein</div>
                            </div>
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.carbohydrate}g</div>
                                <div className="text-muted small">carbs</div>
                            </div>
                            <div className="text-center">
                                <div className="h4 mb-0">{nutrition.fat}g</div>
                                <div className="text-muted small">fat</div>
                            </div>
                        </div>
                        <div className="mt-1">
                            <small className="text-muted">per serving</small>
                        </div>
                        <div className="d-flex align-items-center justify-content-center mt-4 gap-3">
                            <button 
                                className="btn p-0" 
                                onClick={() => setServings(prev => Math.max(1, prev - 1))}
                                style={{ 
                                    border: 'none', 
                                    background: 'none',
                                    color: '#52cd92',
                                    fontSize: '1.5rem',
                                    lineHeight: 1,
                                    fontWeight: 'bold'
                                }}
                            >
                                −
                            </button>
                            <div className="d-flex flex-column align-items-center">
                                <span className="h4 mb-0">{servings}</span>
                                <small className="text-muted">{servings === 1 ? 'serving' : 'servings'}</small>
                            </div>
                            <button 
                                className="btn p-0" 
                                onClick={() => setServings(prev => Math.min(16, prev + 1))}
                                style={{ 
                                    border: 'none', 
                                    background: 'none',
                                    color: '#52cd92',
                                    fontSize: '1.5rem',
                                    lineHeight: 1,
                                    fontWeight: 'bold'
                                }}
                            >
                                +
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-4 w-100 mx-auto" style={{ maxWidth: '1200px' }}>
                    <div className="col-12 col-lg-4 mb-1">
                        <div>
                            <div className="card-header rounded-top">
                                <h3 className="h5 mb-0">Ingredients</h3>
                            </div>
                            <div className="pt-3">
                                <div className="d-flex flex-column gap-2">
                                    {ingredients.map((ingredient, index) => (
                                        <div key={index} className="p-2 border rounded d-flex align-items-center">
                                            <div 
                                                style={{ 
                                                    width: '50px',
                                                    height: '50px',
                                                    marginRight: '1rem',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <img 
                                                    src={getIngredientImageUrl(ingredient.ingredient_id)}
                                                    alt={ingredient.ingredient_name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '4px'
                                                    }}
                                                    onError={(e) => {
                                                        if (e.target.getAttribute('data-error-handled')) return;
                                                        e.target.setAttribute('data-error-handled', 'true');
                                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMjAgMTVIMzBMMzIuNSAxNy41SDM3LjVDMzguODgxIDE3LjUgNDAgMTguNjE5IDQwIDIwVjM1QzQwIDM2LjM4MSAzOC44ODEgMzcuNSAzNy41IDM3LjVIMTIuNUMxMS4xMTkgMzcuNSAxMCAzNi4zODEgMTAgMzVWMjBDMTAgMTguNjE5IDExLjExOSAxNy41IDEyLjUgMTcuNUgxNy41TDIwIDE1Wk0yNSAzNUMyOC44NjYgMzUgMzIgMzEuODY2IDMyIDI4QzMyIDI0LjEzNCAyOC44NjYgMjEgMjUgMjFDMjEuMTM0IDIxIDE4IDI0LjEzNCAxOCAyOEMxOCAzMS44NjYgMjEuMTM0IDM1IDI1IDM1Wk0yNSAzMi41QzIyLjUxNSAzMi41IDIwLjUgMzAuNDg1IDIwLjUgMjhDMjAuNSAyNS41MTUgMjIuNTE1IDIzLjUgMjUgMjMuNUMyNy40ODUgMjMuNSAyOS41IDI1LjUxNSAyOS41IDI4QzI5LjUgMzAuNDg1IDI3LjQ4NSAzMi41IDI1IDMyLjVaIiBmaWxsPSIjOTA5MDkwIi8+PC9zdmc+';
                                                    }}
                                                />
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex align-items-center">
                                                    <span className="fw-bold me-2">
                                                        {formatQuantity(ingredient.quantity * (servings / (recipeData?.servings || 1)))} {ingredient.unit_of_measurement}
                                                    </span>
                                                    <span>{ingredient.ingredient_name}</span>
                                                </div>
                                                {ingredient.notes && (
                                                    <small className="text-muted d-block mt-1">{ingredient.notes}</small>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-8">
                        <div>
                            <div className="card-header rounded-top">
                                <h3 className="h5 mb-0">Recipe Steps</h3>
                            </div>
                            <div className="pt-3">
                                <div className="d-flex flex-column gap-3">
                                    {steps.map((step) => (
                                        <div key={step.id} className="card">
                                            <div className="card-body d-flex gap-3">
                                                <div>
                                                    <span className="badge rounded-pill fs-6" style={{ backgroundColor: '#52cd92' }}>
                                                        {step.step_number}
                                                    </span>
                                                </div>
                                                <div className="flex-grow-1">
                                                    {step.instruction}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* I Cooked This Button at the Bottom of Recipe Steps */}
                                {pgUser && !initialMeal.has_been_cooked && (
                                    <div className="mt-4">
                                        <CompleteMealCookingButton
                                            mealId={initialMeal.id}
                                            appUserId={pgUser.id}
                                            servingSize={servings}
                                            mealName={mealName}
                                            recipeId={meal.recipe_id}
                                            onComplete={onBack}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MealSteps;
